/**
 * InviteMember Component
 *
 * Allows workspace admins to invite new members by email.
 *
 * @module components/workspace/InviteMember
 */

import { useState, useEffect, useCallback, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X, Loader2, Mail, AlertCircle, CheckCircle } from "lucide-react";
import {
  sendInvitation,
  getPendingInvitations,
  revokeInvitation,
} from "@/services/workspace";
import { logError } from "../../services/logger";
import type { WorkspaceRole, WorkspaceInvitation } from "@/types";

/**
 * Role option configuration
 */
/**
 * Keys, not words: this table is module scope, so a t() call here resolves once
 * at import and then holds that language for the session. `value` is the role
 * the RPC receives and is deliberately untouched.
 */
const ROLE_OPTIONS: Array<{
  value: WorkspaceRole;
  labelKey: string;
  descriptionKey: string;
}> = [
  {
    value: "admin",
    labelKey: "workspace.roleAdmin",
    descriptionKey: "workspace.roleAdminDesc",
  },
  {
    value: "analyst",
    labelKey: "workspace.roleAnalyst",
    descriptionKey: "workspace.roleAnalystDesc",
  },
  {
    value: "viewer",
    labelKey: "workspace.roleViewer",
    descriptionKey: "workspace.roleViewerDesc",
  },
];

/** The role a pending invitation carries, as a word rather than its enum. */
const ROLE_LABEL_KEY: Record<string, string> = {
  admin: "workspace.roleAdmin",
  analyst: "workspace.roleAnalyst",
  viewer: "workspace.roleViewer",
};

/**
 * Format date for display
 */
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Check if invitation is expired
 */
const isExpired = (expiresAt: string): boolean => {
  return new Date(expiresAt) < new Date();
};

/**
 * Pending invitation from database
 */
interface PendingInvitation {
  id: string;
  workspace_id: string;
  email: string;
  role: WorkspaceRole;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  workspaces?: { name: string };
}

interface InviteMemberProps {
  workspaceId: string;
  workspaceName?: string;
  onSuccess?: (invitation: WorkspaceInvitation) => void;
}

/**
 * InviteMember Component
 * Provides UI for inviting new workspace members and managing pending invitations
 */
export const InviteMember: React.FC<InviteMemberProps> = ({
  workspaceId,
  workspaceName: _workspaceName,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WorkspaceRole>("viewer");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // The accept link, shown only when nothing emailed it — the recipient has no
  // other way to receive it.
  const [inviteLink, setInviteLink] = useState("");

  // Only once something has been typed: an untouched field is not yet wrong.
  const emailLooksWrong = email.trim().length > 0 && !/\S+@\S+\.\S+/.test(email.trim());
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  /**
   * Load pending invitations for the workspace
   */
  const loadPendingInvitations = useCallback(async (): Promise<void> => {
    if (!workspaceId) return;

    setLoadingInvitations(true);
    setError("");

    try {
      const { success, data, error: invError } = await getPendingInvitations(
        workspaceId
      , t);

      if (!success) {
        setError(invError || t("workspace.loadInvitesFailed"));
      } else {
        setPendingInvitations(data || []);
      }
    } catch (err) {
      logError("InviteMember", "Error loading invitations", err);
      setError(t("workspace.loadInvitesFailed"));
    } finally {
      setLoadingInvitations(false);
    }
  }, [workspaceId, t]);

  // Load pending invitations on mount
  useEffect(() => {
    loadPendingInvitations();
  }, [loadPendingInvitations]);

  /**
   * Handle invite form submission
   */
  const handleSendInvite = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setInviteLink("");
    setIsLoading(true);

    try {
      const { success, data, error: inviteError } = await sendInvitation({
        email: email.trim(),
        role,
        workspaceId,
      }, t);

      if (!success) {
        setError(inviteError || t("workspace.inviteFailed"));
      } else {
        // Only say "sent" when something actually sent it. No email service is
        // wired yet, so the honest message is that the invitation exists and
        // the link has to be passed on by hand — announcing a delivery that
        // never happened leaves the recipient waiting for nothing.
        setSuccessMessage(
          data?.emailed
            ? t("workspace.inviteSentEmail", { email })
            : t("workspace.inviteCreatedNoEmail", { email }),
        );
        setInviteLink(data?.inviteUrl ?? "");
        setEmail("");
        setRole("viewer");
        setShowInviteForm(false);

        // Refresh pending invitations
        await loadPendingInvitations();

        // Callback for parent component
        if (onSuccess && data?.invitation) {
          onSuccess(data.invitation);
        }
      }
    } catch (err) {
      logError("InviteMember", "Error sending invitation", err);
      setError(t("invite.errSendFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle revoking an invitation
   */
  const handleRevoke = async (invitationId: string): Promise<void> => {
    if (!window.confirm(t("workspace.confirmRevoke"))) {
      return;
    }

    setError("");
    // The success banner from the PREVIOUS action was left standing, so a
    // failed revoke rendered its red error directly beneath a green
    // "Invitation created…" — two verdicts on screen, only one of them about
    // what just happened.
    setSuccessMessage("");
    setInviteLink("");
    setIsLoading(true);

    try {
      const { success, error: revokeError } = await revokeInvitation(
        invitationId,
        workspaceId
      , t);

      if (!success) {
        setError(revokeError || t("workspace.revokeFailed"));
      } else {
        setSuccessMessage(t("workspace.invitationRevoked"));
        await loadPendingInvitations();
      }
    } catch (err) {
      logError("InviteMember", "Error revoking invitation", err);
      setError(t("workspace.revokeFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle clearing error message
   */
  const clearError = (): void => {
    setError("");
  };

  /**
   * Handle clearing success message
   */
  const clearSuccess = (): void => {
    setSuccessMessage("");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">{t("workspace.teamMembers")}</h2>
        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-ink-950 text-sm font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-ink-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("workspace.inviteMember")}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-700 mr-2" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
          {/* An icon-only control has no accessible name unless it is given
              one: a screen reader announced "button" with nothing to say what
              it dismisses. */}
          <button
            type="button"
            onClick={clearError}
            aria-label={t("workspace.dismissAlert")}
            className="text-red-700 hover:text-red-800"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="min-w-0 flex-1">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-700 mr-2 shrink-0" />
              <span className="text-sm text-green-800">{successMessage}</span>
            </div>
            {inviteLink && (
              <input
                readOnly
                value={inviteLink}
                onFocus={(e) => e.currentTarget.select()}
                aria-label={t("workspace.invitationLink")}
                className="mt-2 w-full rounded border border-green-300 bg-white px-2 py-1 font-mono text-xs text-green-900"
              />
            )}
          </div>
          <button
            type="button"
            onClick={clearSuccess}
            aria-label={t("workspace.dismissConfirm")}
            className="text-green-700 hover:text-green-800"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Invite Form */}
      {showInviteForm && (
        <form
          onSubmit={handleSendInvite}
          className="bg-white border border-gray-200 rounded-lg p-6"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t("workspace.inviteNewMember")}
          </h3>

          {/* Email Input */}
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("workspace.emailAddress")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-600" />
              {/* The browser's own type="email" gate blocks the submit and
                  says nothing a screen reader can reach. The component states
                  the problem itself, and points the field at it. */}
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                required
                aria-invalid={emailLooksWrong}
                aria-describedby={emailLooksWrong ? "email-error" : undefined}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {emailLooksWrong && (
                <p id="email-error" role="alert" className="mt-1 text-xs text-red-700">
                  {t("workspace.emailInvalid")}
                </p>
              )}
            </div>
          </div>

          {/* Role Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("workspace.selectRole")} <span className="text-red-600">*</span>
            </label>
            <div className="space-y-2">
              {ROLE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={role === option.value}
                    onChange={(e) =>
                      setRole(e.target.value as WorkspaceRole)
                    }
                    // No ring COLOUR here on purpose. `focus:ring-primary-500`
                    // stood here and emitted only `--tw-ring-color` with no
                    // `focus:ring-2` to draw anything — a verified no-op. The
                    // radio keeps its native focus outline, which is visible;
                    // recolouring a ring that never paints would only have made
                    // the next reader think this was handled.
                    className="w-4 h-4 text-ink-900 border-gray-300"
                  />
                  <div className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">
                      {t(option.labelKey)}
                    </span>
                    <span className="block text-xs text-gray-600">
                      {t(option.descriptionKey)}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowInviteForm(false);
                setEmail("");
                setRole("viewer");
                // A refusal from the attempt being abandoned kept reading as
                // the verdict on a form that is no longer there.
                setError("");
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t("workspace.cancel")}
            </button>
            <button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-ink-950 bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-ink-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("workspace.sending")}
                </>
              ) : (
                t("workspace.sendInvitation")
              )}
            </button>
          </div>
        </form>
      )}

      {/* Pending Invitations */}
      {loadingInvitations ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
        </div>
      ) : (
        pendingInvitations.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t("workspace.pendingInvitations")}
            </h3>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className={`flex items-center justify-between p-4 border rounded-md ${
                    isExpired(invitation.expires_at)
                      ? "bg-gray-50 border-gray-200"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-ink-900" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {invitation.email}
                      </p>
                      <p className="text-sm text-gray-600">
                        {t("workspace.roleExpires", {
                          role: ROLE_LABEL_KEY[invitation.role]
                            ? t(ROLE_LABEL_KEY[invitation.role])
                            : invitation.role,
                          date: formatDate(invitation.expires_at),
                        })}
                      </p>
                      {isExpired(invitation.expires_at) && (
                        <p className="text-xs text-red-700 mt-1">{t("workspace.expired")}</p>
                      )}
                    </div>
                  </div>
                  {/* Expired invitations kept their row and lost their only
                      control, so the pending list filled with entries nobody
                      could clear — revokeInvitation deletes them perfectly
                      well. The label says which act it is. */}
                  <button
                    onClick={() => handleRevoke(invitation.id)}
                    disabled={isLoading}
                    className="text-sm text-red-700 hover:text-red-800 font-medium disabled:opacity-50"
                  >
                    {isExpired(invitation.expires_at) ? t("workspace.remove") : t("workspace.revoke")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default InviteMember;
